const TransactionsAdapter = require('./transactions-adapter')
const {filterTransactions, isPaymentPlan} = require('./tools')
const {MATCHING_STATUSES, FAILED_MATCHING_REASONS, BOOKING_STATUSES, MAX_REMITA_FEE, FAILED_BOOKING_REASONS} = require('./constants')
const AutoReconciliation = require('./auto-reconciliation')
const get = require('lodash/get')
const smartId = require('../tools/smart-id')

class PaymentReconciliation {
  constructor (
    state,
    logger,
    pgConnection,
    paymentApi,
    paymentPlansApi,
    quickbooksApi,
    financeApi,
    walletApi,
    invoiceApi,
    orderApi
  ) {
    this.paymentApi = paymentApi
    this.paymentPlansApi = paymentPlansApi
    this.invoiceApi = invoiceApi
    this.financeApi = financeApi
    this.orderApi = orderApi
    this.logger = logger
    const { user = {} } = state
    const username = user.name
    this.pgConnection = pgConnection
    if (pgConnection) {
      this.transactions = new TransactionsAdapter(
        pgConnection,
        username
      )
      this.quickbooksAdapter = quickbooksApi
      this.auto = new AutoReconciliation(quickbooksApi, pgConnection, financeApi, paymentApi, walletApi)
    }
  }

  matchFailed (id, reason) {
    return this.transactions.update({
      matching_status: MATCHING_STATUSES.notMatched,
      failed_reason: reason,
      id: id
    })
  }

  async matchPaymentPlan (paymentDebit, transaction) {
    try {
      const paymentInstallment = await this.paymentPlansApi.installmentAdapter.getOne(paymentDebit.reference, { whereCondition: 'code' })
      const paymentPlan = await this.paymentPlansApi.paymentPlanAdapter.getOne(paymentInstallment.payment_plan_id, { whereCondition: 'id' })
      return this.transactions.update({
        matching_status: MATCHING_STATUSES.matched,
        matched_data: { paymentInstallment, paymentPlan },
        id: transaction.id
      })
    } catch (e) {
      return this.matchFailed(transaction.id, FAILED_MATCHING_REASONS.noPaymentPlan)
    }
  }

  async matchInvoice (paymentDebit, transaction) {
    try {
      const {results: quickbooksInvoice} = await this.quickbooksAdapter.transactions.getList({
        ordering: '-created_at',
        filter: {
          txn_type: 'invoice',
          quickbooks_doc_number: paymentDebit.reference
        }
      })
      if (!quickbooksInvoice.length) {
        return this.matchFailed(transaction.id, FAILED_MATCHING_REASONS.invoiceNotFound)
      }
      if (quickbooksInvoice.length > 1) {
        return this.matchFailed(transaction.id, FAILED_MATCHING_REASONS.multipleLocationWithSameCode)
      }
      if (quickbooksInvoice[0].amount === quickbooksInvoice[0].amount_paid) {
        return this.matchFailed(transaction.id, FAILED_MATCHING_REASONS.invoicePaid)
      }
      // this should never happen
      if (quickbooksInvoice[0].amount < quickbooksInvoice[0].amount_paid) {
        return this.matchFailed(transaction.id, FAILED_MATCHING_REASONS.invalidInvoice)
      }
      return this.transactions.update({
        matching_status: MATCHING_STATUSES.matched,
        matched_data: {paymentDebit, quickbooksInvoice: quickbooksInvoice[0]},
        id: transaction.id
      })
    } catch (e) {
      this.logger.warn(`Failed match transactions-invoice with error:`, e)
      return this.matchFailed(transaction.id, FAILED_MATCHING_REASONS.exceptionError)
    }
  }

  async bookPaymentPlanTransaction ({
    paymentPlan,
    paymentInstallment,
    transaction
  }) {
    try {
      const { deposit } = await this.quickbooksAdapter.createPaymentPlanDeposit({
        transaction,
        paymentPlan,
        paymentInstallment
      })
      await this.transactions.update({
        booking_status: BOOKING_STATUSES.booked,
        failed_reason: null,
        quickbooks_deposit_data: deposit,
        id: transaction.id
      })
    } catch (error) {
      await this.transactions.update({
        booking_status: BOOKING_STATUSES.notBooked,
        failed_reason: error,
        id: transaction.id
      })
      this.logger.warn(`Failed book payment plan ${transaction.id}`, error)
    }
  }

  async bookInvoiceTransaction ({
    transaction,
    quickbooksInvoice
  }) {
    try {
      const totalAmount = (quickbooksInvoice.amount - quickbooksInvoice.amount_paid)
      const remitaFee = (transaction.amount - totalAmount)

      if (remitaFee <= MAX_REMITA_FEE) {
        return this.transactions.update({
          booking_status: BOOKING_STATUSES.notBooked,
          failed_reason: FAILED_BOOKING_REASONS.remitaFeeExceeded,
          id: transaction.id
        })
      }

      const {
        payment,
        quickbooksLocation,
        qboCustomer,
        depositAccount
      } = await this.quickbooksAdapter.createPayment({
        quickbooksInvoice,
        totalAmount
      })

      const {deposit} = await this.quickbooksAdapter.createPaymentInvoiceDeposit({
        transaction,
        quickbooksInvoice,
        qboCustomer,
        quickbooksLocation,
        depositAccount,
        payment,
        totalAmount,
        remitaFee
      })
      await this.transactions.update({
        quickbooks_deposit_data: deposit,
        booking_status: BOOKING_STATUSES.booked,
        id: transaction.id
      })
      const {payment: finalPayment} = await this.quickbooksAdapter.updatePayment({
        quickbooksInvoice,
        payment,
        totalAmount
      })

      await this.transactions.update({
        quickbooks_payment_data: finalPayment,
        booking_status: BOOKING_STATUSES.booked,
        id: transaction.id
      })
    } catch (error) {
      await this.transactions.update({
        booking_status: BOOKING_STATUSES.notBooked,
        failed_reason: error,
        id: transaction.id
      })
      this.logger.warn(`Failed book transaction ${transaction.id}`, error)
    }
  }

  async bookOrderInvoice ({ paymentMetaData, customVatLineCountries }) {
    let { locationUuid, orderDocId, custom_fields: customFields } = paymentMetaData
    const orderIdField = (customFields || []).find(f => f.variable_name === 'Order ID') || {}
    let orderId = get(orderIdField, 'value', null)
    if (!orderId) {
      const { orderId: shortOrderId } = smartId.parse(orderDocId)
      orderId = shortOrderId
    }

    const orderData = await this.orderApi.get({orderId})
    const fsid = get(orderData, 'destinationId', null)
    if (!locationUuid && fsid) {
      const { rows } = await this.pgConnection.query(`
        select uuid from avocado.data_location where fsid = $1 limit 1
      `, [fsid])
      locationUuid = rows[0] && rows[0].uuid
    }

    if (!orderData) {
      this.logger.error({ code: 'Order not found', orderId, orderDocId })
      throw new Error('Order not found for order-invoice creation')
    }

    if (orderData.closedStatus === 'cancelled') {
      this.logger.info({
        code: 'Order has been marked as cancelled',
        result: 'Follow up auto reconciliation will attempt to book payment amount to clients wallet',
        orderData
      })

      return {
        transaction: [],
        orderData: null,
        fsid: null
      }
    }

    // First create an invoice off of the order payment is linked to
    let qboInvoice
    let qboCompanyCode

    // If order is routing or topup we should process only pay_on_delivery products
    if (orderData.orderType === 'routine' || orderData.orderType === 'topup') {
      orderData.products = Object.keys(orderData.products).reduce((acc, productId) => {
        const product = orderData.products[productId]
        const allocationType = get(orderData.products, `${productId}.allocationType`)
        if (allocationType === 'pay_on_delivery') {
          acc[productId] = product
        }
        return acc
      }, {})
    }

    const [pgQboInvoice] = await this.invoiceApi.pg.getQboInvoices({
      orderId: orderId,
      locationId: locationUuid
    })

    if (pgQboInvoice) {
      qboCompanyCode = pgQboInvoice.qboCompanyCode
      qboInvoice = await this.quickbooksAdapter.entities.invoice.get(pgQboInvoice.qboCompanyCode, pgQboInvoice.qboInvoiceId)
    } else {
      const invoiceDate = new Date().toJSON()
      const pgInvoiceId = await this.invoiceApi.pg.insertInvoice({
        invoice: { locationId: locationUuid },
        lines: Object.keys(orderData.products)
          .map(productId => {
            const product = orderData.products[productId]
            const sku = productId.split(':')[1]
            return {
              orderInvoicingType: 'prepaid',
              eventType: 'delivery',
              sku,
              date: invoiceDate,
              quantity: product.adjusted,
              bonus: 0,
              orderId: orderId,
              reconciled: false
            }
          }).filter(p => p)
      })

      const { invoice, lines } = await this.invoiceApi.pg.getInvoice(pgInvoiceId)
      const result = await this.invoiceApi.auto.createInvoiceAndCredit(
        pgInvoiceId,
        { invoice, lines, customVatLineCountries }
      )
      qboInvoice = result.qboInvoice
      qboCompanyCode = result.qboCompanyCode
    }

    // sync invoice data to fin schema
    await this.financeApi.sync.syncQboObjs(this.pgConnection, qboCompanyCode, { invoice: [qboInvoice] }, this.quickbooksAdapter.entities)

    return {
      transaction: [{ txnId: qboInvoice.Id, txnType: 'invoice' }],
      orderData,
      fsid,
      locationUuid
    }
  }

  async doMatch (transaction) {
    try {
      const paymentDebit = await this.paymentApi.paymentDebit.getOne(transaction.transactionRef, { whereCondition: `"data"->'remitaResponse'->'body'->'transactionRef'` })
      if (isPaymentPlan(paymentDebit.reference)) {
        return this.matchPaymentPlan(paymentDebit, transaction)
      }
      return this.matchInvoice(paymentDebit, transaction)
    } catch (e) {
      return this.matchFailed(transaction.id, FAILED_MATCHING_REASONS.paymentDebitNotFound)
    }
  }

  async match () {
    const {results: transactions} = await this.transactions.getList({
      ordering: '-created_at',
      filter: {
        matching_status: null
      }
    })
    const {matchable, notMatchable} = filterTransactions(transactions)
    await Promise.all([
      ...matchable.map((val) => this.doMatch(val)),
      // we can immediately update transactions which are not matched
      ...notMatchable.map(val => this.matchFailed(val.id, FAILED_MATCHING_REASONS.unknownNarration))
    ])

    return true
  }
}

module.exports = PaymentReconciliation
